import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/Pages.css";
import "../styles/WhatsNew.css";

const YOUTUBE_API_KEY =
  process.env.REACT_APP_YOUTUBE_API_KEY ||
  "AIzaSyCfKPm1GW8SwqE9AZM9p17fOYPPkZtNWIY";
const YOUTUBE_API_BASE =
  process.env.REACT_APP_YOUTUBE_API_BASE ||
  "https://www.googleapis.com/youtube/v3";
const LOOKBACK_DAYS = 15;

const CHANNELS = [
  {
    name: "iHariPrabodham",
    handle: "@iHariPrabodham",
    url: "https://www.youtube.com/@iHariPrabodham",
  },
  {
    name: "Hari Na Das",
    handle: "@sharanagat936",
    url: "https://www.youtube.com/@sharanagat936",
  },
  {
    name: "Harisumiran",
    handle: "@harisumiran-nj",
    url: "https://www.youtube.com/@harisumiran-nj",
  },
  {
    name: "YDS Canada",
    handle: "@ydscanada",
    url: "https://www.youtube.com/@ydscanada",
  },
  {
    name: "HariPrabodham UK",
    handle: "@HariPrabodhamUK",
    url: "https://www.youtube.com/@HariPrabodhamUK",
  },
  {
    name: "Harisumiran DE",
    handle: "@harisumiranDE",
    url: "https://www.youtube.com/@harisumiranDE",
  },
  {
    name: "HariPrabodham NZ",
    handle: "@hariprabodhamnz",
    url: "https://www.youtube.com/@hariprabodhamnz",
  },
];

function getCutoffDate() {
  const date = new Date();
  date.setDate(date.getDate() - LOOKBACK_DAYS);
  return date;
}

function isNewVideo(publishedAt, cutoffDate) {
  if (!publishedAt) return false;
  return new Date(publishedAt) >= cutoffDate;
}

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`YouTube API request failed (${response.status})`);
  }
  return response.json();
}

async function fetchChannelRecentVideos(channel, cutoffDate) {
  const cleanHandle = channel.handle.replace(/^@/, "");
  const channelUrl = `${YOUTUBE_API_BASE}/channels?part=snippet,contentDetails&forHandle=${encodeURIComponent(
    cleanHandle,
  )}&key=${encodeURIComponent(YOUTUBE_API_KEY)}`;

  const channelResponse = await fetchJson(channelUrl);
  const channelData = channelResponse.items && channelResponse.items[0];

  if (!channelData) {
    return [];
  }

  const uploadsPlaylistId =
    channelData.contentDetails?.relatedPlaylists?.uploads || null;
  const channelTitle = channelData.snippet?.title || channel.name;

  if (!uploadsPlaylistId) {
    return [];
  }

  const videos = [];
  let pageToken = "";
  let keepPaging = true;

  while (keepPaging) {
    const playlistUrl =
      `${YOUTUBE_API_BASE}/playlistItems?part=snippet,contentDetails` +
      `&playlistId=${encodeURIComponent(uploadsPlaylistId)}` +
      `&maxResults=50` +
      `&key=${encodeURIComponent(YOUTUBE_API_KEY)}` +
      (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : "");

    const playlistResponse = await fetchJson(playlistUrl);
    const items = playlistResponse.items || [];

    if (items.length === 0) {
      break;
    }

    for (const item of items) {
      const publishedAt =
        item.contentDetails?.videoPublishedAt || item.snippet?.publishedAt;
      const videoId = item.contentDetails?.videoId;
      if (!videoId) {
        continue;
      }

      if (isNewVideo(publishedAt, cutoffDate)) {
        videos.push({
          id: videoId,
          title: item.snippet?.title || "Untitled Video",
          channelName: channelTitle,
          channelUrl: channel.url,
          publishedAt,
          thumbnail:
            item.snippet?.thumbnails?.high?.url ||
            item.snippet?.thumbnails?.medium?.url ||
            item.snippet?.thumbnails?.default?.url ||
            "",
          videoUrl: `https://www.youtube.com/watch?v=${videoId}`,
        });
      }
    }

    const oldestItem = items[items.length - 1];
    const oldestPublishedAt =
      oldestItem?.contentDetails?.videoPublishedAt ||
      oldestItem?.snippet?.publishedAt;

    if (
      !playlistResponse.nextPageToken ||
      !isNewVideo(oldestPublishedAt, cutoffDate)
    ) {
      keepPaging = false;
    } else {
      pageToken = playlistResponse.nextPageToken;
    }
  }

  return videos;
}

function WhatsNew() {
  const [channelVideos, setChannelVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const cutoffDate = useMemo(() => getCutoffDate(), []);

  const loadVideos = useCallback(async () => {
    if (!YOUTUBE_API_KEY) {
      setError("YouTube API key is missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const results = await Promise.allSettled(
        CHANNELS.map((channel) =>
          fetchChannelRecentVideos(channel, cutoffDate),
        ),
      );

      const groupedVideos = [];
      const failedChannels = [];

      results.forEach((result, index) => {
        const channel = CHANNELS[index];
        if (result.status === "fulfilled") {
          const videos = [...result.value].sort(
            (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt),
          );
          groupedVideos.push({
            channelName: videos[0]?.channelName || channel.name,
            channelUrl: channel.url,
            videos,
          });
        } else {
          groupedVideos.push({
            channelName: channel.name,
            channelUrl: channel.url,
            videos: [],
          });
          failedChannels.push(channel.name);
        }
      });

      setChannelVideos(groupedVideos);

      if (failedChannels.length > 0) {
        setError(
          `Could not load: ${failedChannels.join(", ")}. Showing available channels.`,
        );
      }

      setUpdatedAt(new Date());
    } catch (e) {
      setError("Failed to load videos from YouTube API.");
      setChannelVideos([]);
    } finally {
      setLoading(false);
    }
  }, [cutoffDate]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const totalVideos = useMemo(
    () => channelVideos.reduce((sum, group) => sum + group.videos.length, 0),
    [channelVideos],
  );

  return (
    <main className="page-content whats-new-page">
      <section className="page-header">
        <h1 className="section-title">What&apos;s New</h1>
        <p className="header-subtitle">
          Latest uploads from all channels in the last {LOOKBACK_DAYS} days
        </p>
      </section>

      <section className="whats-new-toolbar">
        <div>
          <strong>{totalVideos}</strong> recent video
          {totalVideos === 1 ? "" : "s"}
        </div>
        <button className="refresh-btn" onClick={loadVideos} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </section>

      {updatedAt && (
        <p className="whats-new-meta">Updated: {updatedAt.toLocaleString()}</p>
      )}

      {error && <p className="whats-new-error" role="alert">{error}</p>}

      {loading ? (
        <div className="loading" role="status">Loading recent videos...</div>
      ) : totalVideos === 0 ? (
        <div className="whats-new-empty" role="status">
          No videos found in the last {LOOKBACK_DAYS} days.
        </div>
      ) : (
        <div className="whats-new-channel-list">
          {channelVideos.map((group) => (
            <section
              className="whats-new-channel-section"
              key={group.channelName}
            >
              <div className="whats-new-channel-header">
                <h2>{group.channelName}</h2>
                <a
                  href={group.channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Channel
                </a>
              </div>

              {group.videos.length === 0 ? (
                <p className="whats-new-channel-empty">
                  No new videos in last {LOOKBACK_DAYS} days.
                </p>
              ) : (
                <div className="whats-new-grid">
                  {group.videos.map((video) => (
                    <article
                      className="whats-new-card"
                      key={`${video.id}-${video.publishedAt}`}
                    >
                      <a
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="whats-new-thumb"
                        />
                      </a>
                      <div className="whats-new-content">
                        <h3>
                          <a
                            href={video.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {video.title}
                          </a>
                        </h3>
                        <p>{new Date(video.publishedAt).toLocaleString()}</p>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </main>
  );
}

export default WhatsNew;
